import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { MessageCircle, Loader2, Send } from 'lucide-react';

/**
 * Reusable "send a WhatsApp message" dialog. Starts (or continues) a conversation
 * by phone number. Falls back to an approved-template picker when the 24h window
 * is closed.
 *
 * Props: open, onOpenChange, phone (prefill), customerId, customerName, lockPhone, defaultParams, onSent
 */
export const WhatsAppComposeDialog = ({ open, onOpenChange, phone = '', customerId = '', customerName = '', lockPhone = false, defaultParams = [], onSent }) => {
  const [to, setTo] = useState(phone);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState('text'); // 'text' | 'template'
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState('');
  const [selected, setSelected] = useState(null);
  const [params, setParams] = useState([]);

  useEffect(() => {
    if (open) {
      setTo(phone || '');
      setText('');
      setMode('text');
      setSelected(null);
      setParams([]);
      setTemplatesError('');
    }
  }, [open, phone]);

  const loadTemplates = async () => {
    setMode('template');
    setSelected(null);
    setParams([]);
    setTemplatesLoading(true);
    setTemplatesError('');
    try {
      const { data } = await api.get('/whatsapp/templates');
      setTemplates(data.templates || []);
      if (data.error) setTemplatesError(data.error);
    } catch (e) {
      setTemplatesError(e.response?.data?.detail || 'Could not load templates');
    } finally { setTemplatesLoading(false); }
  };

  const sendText = async () => {
    if (!to.trim() || !text.trim() || sending) return;
    setSending(true);
    try {
      await api.post('/whatsapp/compose/send-text', { phone: to.trim(), text: text.trim(), customer_id: customerId || '' });
      toast.success('WhatsApp message sent');
      onSent?.();
      onOpenChange(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = typeof detail === 'object' ? detail.code : null;
      if (e.response?.status === 409 || code === 'TEMPLATE_REQUIRED') {
        toast.info('Outside the 24-hour window — pick an approved template to start the chat.');
        loadTemplates();
      } else {
        toast.error((typeof detail === 'object' ? detail.message : detail) || 'Could not send message');
      }
    } finally { setSending(false); }
  };

  const pick = (tpl) => {
    setSelected(tpl);
    // Pre-fill variable values from context (e.g. customer name, install date).
    setParams(Array.from({ length: tpl.param_count || 0 }, (_, i) => defaultParams[i] || ''));
  };

  const sendTemplate = async () => {
    if (!to.trim() || !selected || sending) return;
    if ((selected.param_count || 0) > 0 && params.some((p) => !p.trim())) {
      toast.error('Fill in all template values first');
      return;
    }
    setSending(true);
    try {
      await api.post('/whatsapp/compose/send-template', {
        phone: to.trim(), customer_id: customerId || '',
        name: selected.name, language_code: selected.language || 'en_US', body_values: params,
      });
      toast.success('Template message sent');
      onSent?.();
      onOpenChange(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === 'object' ? detail.message : detail) || 'Could not send template');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="wa-compose-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-emerald-600" /> Message on WhatsApp
          </DialogTitle>
          <DialogDescription>
            {customerName ? `Send a WhatsApp message to ${customerName}.` : 'Send a WhatsApp message.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          <div>
            <Label className="text-xs text-slate-500">To (mobile number)</Label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} disabled={lockPhone}
              placeholder="e.g. 447700900123" data-testid="wa-compose-phone" />
          </div>

          {mode === 'text' ? (
            <div>
              <Label className="text-xs text-slate-500">Message</Label>
              <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4}
                placeholder="Type your message…" data-testid="wa-compose-text" />
              <p className="text-[11px] text-slate-400 mt-1">
                Free-form messages only work within 24h of the customer's last message. Otherwise we'll switch to an approved template.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-slate-500">Choose an approved template</Label>
                <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setMode('text')}>Back to text</Button>
              </div>
              {templatesLoading ? (
                <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
              ) : templatesError ? (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="wa-compose-tpl-error">{templatesError}</div>
              ) : templates.length === 0 ? (
                <div className="rounded-lg bg-slate-50 border p-4 text-sm text-slate-500 text-center">
                  No approved templates yet. Create &amp; get one approved in Meta Business Manager.
                </div>
              ) : !selected ? (
                <div className="space-y-2" data-testid="wa-compose-tpl-list">
                  {templates.map((t) => (
                    <button key={`${t.name}-${t.language}`} onClick={() => pick(t)}
                      className="w-full text-left p-3 rounded-lg border hover:border-emerald-400 hover:bg-emerald-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm text-slate-800">{t.name}</span>
                        <Badge variant="outline" className="text-[10px]">{t.language} · {t.category}</Badge>
                      </div>
                      {t.body_text && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{t.body_text}</p>}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-2" data-testid="wa-compose-tpl-selected">
                  <div className="p-3 rounded-lg border bg-slate-50">
                    <p className="font-medium text-sm text-slate-800">{selected.name}</p>
                    {selected.body_text && <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{selected.body_text}</p>}
                  </div>
                  {(selected.param_count || 0) > 0 && params.map((val, i) => (
                    <Input key={i} placeholder={`Value for {{${i + 1}}}`} value={val}
                      onChange={(e) => setParams((prev) => prev.map((p, idx) => (idx === i ? e.target.value : p)))} />
                  ))}
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setSelected(null)}>Choose a different template</Button>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {mode === 'text' ? (
            <>
              <Button variant="outline" onClick={loadTemplates} disabled={sending}
                className="mr-auto" data-testid="wa-compose-use-template">
                Use a template
              </Button>
              <Button onClick={sendText} disabled={sending || !to.trim() || !text.trim()}
                className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-compose-send">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send
              </Button>
            </>
          ) : (
            <Button onClick={sendTemplate} disabled={sending || !selected}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-compose-send-template">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send template
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsAppComposeDialog;
